import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import Swal from 'sweetalert2';

import UserNav from '../../components/navbar/user-nav';
import AdminSidebar from '../../components/admin-sidebar';
import Footer from '../../components/footer';
import FooterTop from '../../components/footer-top';
import PageTitle from '../../components/page-title';
import { LoadingMessage, EmptyState, SkeletonTable } from '../../components/loading-states';

interface Direccion {
  calle?: string;
  numero?: string;
  ciudad?: string;
  estado?: string;
  zona?: string;
  area?: string;
  unidad?: string;
  codigo_postal?: string;
}

interface Propiedad {
  id: string;
  nombre?: string;
  titulo?: string;
  direccion: string | Direccion;
  ciudad?: string;
  area?: string;
  comunidad?: string;
  precio?: number;
  precio_actual_usd?: number;
  precio_original_usd?: number;
  estado?: string;
  imagen?: string;
  fotos?: string[];
  descripcion?: string;
  tipo_operacion?: string;
  operacion?: string;
  tipo_propiedad?: string;
  tipo?: string;
  categoria?: string;
  tamaño?: string;
  estatus_publicacion?: string;
  activo?: boolean;
}

// Lee el estatus MLS tal cual Flex lo entrega. La plataforma es SOLO canal de visualización:
// no categorizamos, no renombramos, no pausamos. Sólo color del badge para legibilidad.
function getFlexStatus(p: any): string {
  const df = p?.datos_flex || {};
  return String(df.standard_status || df.mls_status || '').trim();
}

// Color UX derivado del valor Flex (no cambia el valor mostrado).
function getFlexStatusBadgeClass(status: string): string {
  const s = status.toLowerCase();
  if (s === 'active') return 'bg-success';
  if (s === 'pending' || s.includes('contract')) return 'bg-warning text-dark';
  if (s === 'closed' || s === 'sold') return 'bg-danger';
  if (s === 'hold' || s === 'withdrawn' || s === 'expired' || s.includes('canceled') || s.includes('cancelled') || s.includes('off market')) return 'bg-secondary';
  return 'bg-light text-dark border';
}

export default function MyProperty() {
  const [show, setShow] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [propiedades, setPropiedades] = useState<Propiedad[]>([]);
  const [propiedadesAgencia, setPropiedadesAgencia] = useState<Propiedad[]>([]);
  const [loading, setLoading] = useState(true);
  // Filtro por valor Flex tal cual (cualquier string); "" = Todas.
  const [filtroEstatus, setFiltroEstatus] = useState<string>('');
  // Vista: mis propiedades asignadas vs todas las de la agencia del usuario.
  // Si el usuario no tiene propias, se abre por default en "agencia" (no dejar pantalla vacía).
  const [vista, setVista] = useState<'propias' | 'agencia'>('propias');

  // 1. Obtener usuario
  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), async (fbUser) => {
      if (!fbUser?.email) return;

      // Buscar en asociado
      let snap = await getDocs(query(collection(db, 'asociado'), where('correo', '==', fbUser.email)));
      if (!snap.empty) {
        const data = snap.docs[0].data();
        const id = snap.docs[0].id;
        const inmobiliariaDocId = data.inmobiliaria_doc_id; // Usar doc.id

        if (process.env.NODE_ENV === "development") console.log('👤 Asociado encontrado:', data.nombre);
        if (process.env.NODE_ENV === "development") console.log('🏢 inmobiliaria_doc_id:', inmobiliariaDocId);

        // Obtener nombre de inmobiliaria usando doc.id
        let nombreInmo = '';
        if (inmobiliariaDocId) {
          const { doc: docFn, getDoc } = await import('firebase/firestore');
          const inmoDocRef = docFn(db, 'inmobiliaria', inmobiliariaDocId);
          const inmoSnap = await getDoc(inmoDocRef);
          if (inmoSnap.exists()) {
            nombreInmo = inmoSnap.data().nombre || '';
            if (process.env.NODE_ENV === "development") console.log('✅ Inmobiliaria encontrada:', nombreInmo);
          } else {
            if (process.env.NODE_ENV === "development") console.warn('⚠️ Inmobiliaria no encontrada con doc.id:', inmobiliariaDocId);
          }
        }

        setUser({ ...data, tipo: 'asociado', id, inmobiliaria_nombre: nombreInmo });
        return;
      }

      // Buscar en asesor
      snap = await getDocs(query(collection(db, 'asesor'), where('correo', '==', fbUser.email)));
      if (!snap.empty) {
        const data = snap.docs[0].data();
        const id = snap.docs[0].id;
        const inmobiliariaDocId = Array.isArray(data.inmobiliaria_doc_ids)
          ? data.inmobiliaria_doc_ids[0]
          : data.inmobiliaria_doc_ids;

        if (process.env.NODE_ENV === "development") console.log('👤 Asesor encontrado:', data.nombre);
        if (process.env.NODE_ENV === "development") console.log('🏢 inmobiliaria_doc_ids:', inmobiliariaDocId);

        // Obtener nombre de inmobiliaria usando doc.id
        let nombreInmo = '';
        if (inmobiliariaDocId) {
          const { doc: docFn, getDoc } = await import('firebase/firestore');
          const inmoDocRef = docFn(db, 'inmobiliaria', inmobiliariaDocId);
          const inmoSnap = await getDoc(inmoDocRef);
          if (inmoSnap.exists()) {
            nombreInmo = inmoSnap.data().nombre || '';
            if (process.env.NODE_ENV === "development") console.log('✅ Inmobiliaria encontrada:', nombreInmo);
          } else {
            if (process.env.NODE_ENV === "development") console.warn('⚠️ Inmobiliaria no encontrada con doc.id:', inmobiliariaDocId);
          }
        }

        setUser({ ...data, tipo: 'asesor', id, inmobiliaria_nombre: nombreInmo });
      }
    });

    return () => unsub();
  }, []);

  // 2. Cargar propiedades usando doc.id de Firestore
useEffect(() => {
  const cargarPropiedades = async () => {
    if (!user) return;

    try {
      if (process.env.NODE_ENV === "development") console.log('🔍 Buscando propiedades para:', {
        tipo: user.tipo,
        nombre: user.nombre,
        docId: user.id
      });

      // Usar el doc.id de Firestore del asesor/asociado
      // Traer TODAS las propiedades (activas, pendientes, rechazadas)
      const q = query(
        collection(db, 'propiedades'),
        where('asesor_doc_ids', 'array-contains', user.id)
      );

      const snap = await getDocs(q);
      const list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Propiedad))
        .filter(p => (p as any).eliminado !== true);

      if (process.env.NODE_ENV === "development") console.log(`✅ Propiedades propias: ${list.length}`);

      setPropiedades(list);

      // También cargar todas las propiedades de la agencia del usuario.
      // Si el user no tiene propias, abrimos por default la vista "agencia".
      const inmoId = user.inmobiliaria_doc_id;
      if (inmoId) {
        try {
          const qAgencia = query(
            collection(db, 'propiedades'),
            where('inmobiliaria_doc_id', '==', inmoId)
          );
          const snapAg = await getDocs(qAgencia);
          const listAg = snapAg.docs
            .map(doc => ({ id: doc.id, ...doc.data() } as Propiedad))
            .filter(p => (p as any).eliminado !== true);
          setPropiedadesAgencia(listAg);
          if (process.env.NODE_ENV === "development") console.log(`✅ Propiedades de la agencia: ${listAg.length}`);
        } catch (e) {
          if (process.env.NODE_ENV === "development") console.warn('⚠️ No se cargaron propiedades de agencia:', e);
        }
      }

      if (list.length === 0) setVista('agencia');
    } catch (error) {
      console.error('❌ Error al obtener propiedades:', error);
    } finally {
      setLoading(false);
    }
  };

  cargarPropiedades();
}, [user]);

  // Función para cambiar el estado de la propiedad
  const handleStatusChange = async (propiedadId: string, nuevoEstado: string) => {
    try {
      // Sprint 9 (F3-003): verificar ownership antes de updateDoc.
      // ADMIN cambia cualquier propiedad; otros roles sólo las propias.
      const propiedad = propiedades.find(p => p.id === propiedadId);
      if (!propiedad) throw new Error('Propiedad no encontrada');

      const esAdmin = user?.rol === 'ADMIN';
      if (!esAdmin) {
        const asesorDocIds = Array.isArray((propiedad as any).asesor_doc_ids) ? (propiedad as any).asesor_doc_ids : [];
        const inmoDocId = (propiedad as any).inmobiliaria_doc_id;
        const esOwnerAsesor = user?.id && asesorDocIds.includes(user.id);
        const esOwnerAsociado = user?.rol === 'ASOCIADO' && inmoDocId && user?.inmobiliaria_doc_id === inmoDocId;
        if (!esOwnerAsesor && !esOwnerAsociado) {
          throw new Error('No tienes permisos para modificar esta propiedad');
        }
      }

      const propRef = doc(db, 'propiedades', propiedadId);
      const activo = nuevoEstado === 'Active';

      await updateDoc(propRef, {
        estatus_publicacion: nuevoEstado.toLowerCase(),
        activo: activo
      });

      // Actualizar el estado local
      setPropiedades(prev =>
        prev.map(prop =>
          prop.id === propiedadId
            ? { ...prop, estatus_publicacion: nuevoEstado.toLowerCase(), activo }
            : prop
        )
      );

      Swal.fire({
        icon: 'success',
        title: 'Estado actualizado',
        text: `La propiedad ahora está en estado: ${nuevoEstado}`,
        confirmButtonColor: '#3085d6',
        timer: 2000
      });
    } catch (error) {
      console.error('Error al actualizar estado:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo actualizar el estado de la propiedad',
        confirmButtonColor: '#d33'
      });
    }
  };



  return (
    <>
      <UserNav
        nombreUsuario={user?.nombre || 'Desconocido'}
        nombreInmobiliaria={user?.inmobiliaria_nombre || 'Inmobiliaria no asignada'}
      />

      <PageTitle
        title="Mis Propiedades Publicadas"
        subtitle="Consulta, edita o destaca tus propiedades en venta o renta dentro del portal AMPI."
      />

      <section className="bg-light">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12 col-md-12">
              <div className="filter_search_opt">
                <button className="btn btn-dark full-width mb-4" onClick={() => setShow(!show)}>
                  Dashboard Navigation<i className="fa-solid fa-bars ms-2"></i>
                </button>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-3 col-md-12">
              <AdminSidebar
                show={show}
                setShow={setShow}
                nombre={user?.nombre}
                inmobiliaria={user?.inmobiliaria_nombre}
              />
            </div>

            <div className="col-lg-9 col-md-12">
              <div className="dashboard-wraper">
                {/* Tabs: Mis propiedades / Mi agencia */}
                <ul className="nav nav-tabs mb-3" role="tablist">
                  <li className="nav-item" role="presentation">
                    <button
                      type="button"
                      className={`nav-link ${vista === 'propias' ? 'active' : ''}`}
                      onClick={() => setVista('propias')}
                      disabled={propiedades.length === 0}
                      title={propiedades.length === 0 ? 'No tienes propiedades asignadas' : ''}
                    >
                      <i className="fa-solid fa-user-tag me-2"></i>
                      Mis propiedades ({propiedades.length})
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      type="button"
                      className={`nav-link ${vista === 'agencia' ? 'active' : ''}`}
                      onClick={() => setVista('agencia')}
                      disabled={propiedadesAgencia.length === 0}
                      title={propiedadesAgencia.length === 0 ? 'Sin propiedades en tu agencia' : ''}
                    >
                      <i className="fa-solid fa-building me-2"></i>
                      Mi agencia ({propiedadesAgencia.length})
                    </button>
                  </li>
                </ul>

                <div className="form-submit mb-4">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                    <h4 className="mb-0">
                      {vista === 'propias' ? 'Mis Propiedades' : `Propiedades de ${user?.inmobiliaria_nombre || 'mi agencia'}`}
                    </h4>

                    {/* Filtro por estatus */}
                    <div className="btn-group" role="group">
                      {(() => {
                        // Filtros derivados dinámicamente de los valores que Flex envía.
                        // No categorizamos: si Flex manda 7 estatus distintos, salen 7 botones.
                        const listaActiva = vista === 'propias' ? propiedades : propiedadesAgencia;
                        const conteo = new Map<string, number>();
                        listaActiva.forEach(p => {
                          const s = getFlexStatus(p) || '(sin dato)';
                          conteo.set(s, (conteo.get(s) || 0) + 1);
                        });
                        const estatusPresentes = Array.from(conteo.entries()).sort((a, b) => b[1] - a[1]);

                        return (
                          <>
                            <button
                              type="button"
                              className={`btn btn-sm ${filtroEstatus === '' ? 'btn-primary' : 'btn-outline-primary'}`}
                              onClick={() => setFiltroEstatus('')}
                            >
                              Todas ({listaActiva.length})
                            </button>
                            {estatusPresentes.map(([status, count]) => {
                              const active = filtroEstatus === status;
                              const cls = getFlexStatusBadgeClass(status);
                              const outlineCls = cls.replace('bg-', 'btn-outline-').replace(' text-dark', '').replace(' border', '');
                              const solidCls = cls.replace('bg-', 'btn-').replace(' text-dark', '').replace(' border', '');
                              return (
                                <button
                                  key={status}
                                  type="button"
                                  className={`btn btn-sm ${active ? solidCls : outlineCls}`}
                                  onClick={() => setFiltroEstatus(active ? '' : status)}
                                  title={`Estatus Flex: ${status}`}
                                >
                                  {status} ({count})
                                </button>
                              );
                            })}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {loading ? (
                  <SkeletonTable rows={5} columns={6} />
                ) : (vista === 'propias' ? propiedades : propiedadesAgencia).length === 0 ? (
                  <EmptyState
                    icon="fa-solid fa-building"
                    title={vista === 'propias' ? 'Sin propiedades asignadas' : 'Sin propiedades en tu agencia'}
                    message={
                      vista === 'propias'
                        ? 'No tienes propiedades asignadas. Revisa la pestaña "Mi agencia" para ver las disponibles.'
                        : 'Las propiedades provienen automáticamente del sistema MLS (Flex). Si esperas ver propiedades, contacta al administrador.'
                    }
                  />
                ) : (
                  <div className="row">
                    {(vista === 'propias' ? propiedades : propiedadesAgencia)
                      .filter((item: any) => {
                        if (!filtroEstatus) return true;
                        const s = getFlexStatus(item) || '(sin dato)';
                        return s === filtroEstatus;
                      })
                      .map((item, index) => (
                      <div className="col-md-12 mb-3" key={index}>
                        <div className="card shadow-sm">
                          <div className="row g-0">
                            {/* Imagen de la propiedad */}
                            <div className="col-md-4">
                              {item.fotos && item.fotos.length > 0 && item.fotos[0] ? (
                                <img
                                  src={item.fotos[0]}
                                  className="img-fluid h-100 w-100"
                                  alt={item.titulo || item.nombre}
                                  style={{ objectFit: 'cover', minHeight: '200px', maxHeight: '250px' }}
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.src = '/default.jpg';
                                  }}
                                />
                              ) : (
                                <div
                                  className="bg-secondary d-flex align-items-center justify-content-center h-100"
                                  style={{ minHeight: '200px' }}
                                >
                                  <i className="fas fa-home fa-3x text-white"></i>
                                </div>
                              )}
                            </div>

                            {/* Información de la propiedad */}
                            <div className="col-md-8">
                              <div className="card-body d-flex flex-column h-100">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                  <h5 className="card-title mb-0">
                                    <Link to={`/view-property/${item.id}`} className="text-primary text-decoration-none">
                                      {item.titulo || item.nombre || 'Propiedad sin título'}
                                    </Link>
                                  </h5>
                                  <div className="action d-flex gap-2 align-items-center">
                                    {/* Badge con el estatus Flex tal cual. Plataforma = canal, no transforma. */}
                                    {(() => {
                                      const status = getFlexStatus(item);
                                      if (!status) return null;
                                      return (
                                        <span className={`badge ${getFlexStatusBadgeClass(status)}`} title="Estatus sincronizado desde FlexMLS">
                                          {status}
                                        </span>
                                      );
                                    })()}
                                    <Link to={`/view-property/${item.id}`} title="Ver detalle" className="btn btn-sm btn-outline-primary">
                                      <i className="fa-regular fa-eye"></i>
                                    </Link>
                                  </div>
                                </div>

                                {/* Ubicación */}
                                <p className="mb-2">
                                  <i className="fa-solid fa-map-marker-alt me-2 text-primary"></i>
                                  <span>
                                    {item.area ||
                                     item.comunidad ||
                                     item.ciudad ||
                                     (typeof item.direccion === 'object' && item.direccion
                                       ? `${item.direccion.calle || ''} ${item.direccion.numero || ''}, ${item.direccion.ciudad || item.direccion.area || ''}`.trim()
                                       : typeof item.direccion === 'string'
                                       ? item.direccion
                                       : 'Ubicación no especificada')}
                                  </span>
                                  {item.area && item.comunidad && <span> - {item.comunidad}</span>}
                                </p>

                                {/* Descripción */}
                                {item.descripcion && (
                                  <p
                                    className="mb-2"
                                    style={{
                                      fontSize: '0.9rem',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      color: '#000'
                                    }}
                                    title={item.descripcion}
                                  >
                                    {item.descripcion}
                                  </p>
                                )}

                                {/* Badges: tipo de operación (Venta/Renta) y tipo de propiedad.
                                    El estatus MLS ya se muestra arriba (columna derecha), no duplicamos. */}
                                <div className="mb-2">
                                  {(item.tipo_operacion || item.operacion) && (
                                    <span
                                      className={`badge me-2 ${
                                        (item.tipo_operacion || item.operacion || '').toLowerCase().includes('venta')
                                          ? 'bg-success'
                                          : 'bg-info'
                                      }`}
                                    >
                                      {item.tipo_operacion || item.operacion}
                                    </span>
                                  )}
                                  {(item.tipo_propiedad || item.tipo || item.categoria) && (
                                    <span className="badge bg-secondary">
                                      {item.tipo_propiedad || item.tipo || item.categoria}
                                    </span>
                                  )}
                                </div>

                                {/* Precio */}
                                <p className="fw-bold text-primary mb-0" style={{ fontSize: '1.1rem' }}>
                                  {item.precio_actual_usd
                                    ? `$${item.precio_actual_usd.toLocaleString()} USD`
                                    : item.precio_original_usd
                                    ? `$${item.precio_original_usd.toLocaleString()} USD`
                                    : item.precio
                                    ? `$${item.precio.toLocaleString()}`
                                    : 'Precio no disponible'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
